/* ---------------------------------------------------------------------------
   <m-stat-reveal>
   Spec: claude/spacial-feature-section-spec.md

   Three jobs:
   1. write a single custom property, --p (0 to 1), on itself while it is near
      the viewport. Every scroll-driven effect in stat-reveal.css derives from
      that one number, so a frame costs one rect read and one property write on
      one element no matter how many things are animating.
   2. split the statement into its rendered lines, so each line owns its own
      reveal slice. Re-split whenever the box or the fonts change.
   3. count the stat numbers up once, and start the video once, on entry.

   The scroll listener only exists while an IntersectionObserver says the
   section is near. Off-screen, this element costs nothing.

   EVERYTHING IS INSIDE THE GUARD BELOW ON PURPOSE. The section emits its own
   <script src> tag, so a page with two Stat reveal sections runs this file
   twice, and a top-level `class MStatReveal` throws
   "SyntaxError: Identifier 'MStatReveal' has already been declared" on the
   second run. A class expression inside the guard creates no global binding,
   so re-running the file is a no-op.
   --------------------------------------------------------------------------- */

if (!customElements.get('m-stat-reveal')) {
  customElements.define(
    'm-stat-reveal',
    class extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;

        this.valueEls = Array.from(this.querySelectorAll('[data-countup]'));
        this.statementEl = this.querySelector('[data-split]');
        this.video = this.querySelector('video');
        this.counted = false;
        this.ticking = false;
        this.frame = null;
        this.lastWidth = 0;

        this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        /* no observer support, or motion turned off: show the finished state
           and stop. Never leave content hidden, never autoplay. */
        if (this.reduced || !('IntersectionObserver' in window)) {
          this.classList.add('is-in');
          this.showFinalValues();
          return;
        }

        this.onScroll = this.onScroll.bind(this);
        this.update = this.update.bind(this);
        this.splitStatement = this.splitStatement.bind(this);

        this.splitStatement();

        /* line breaks computed against a fallback font are the wrong line
           breaks */
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.splitStatement);
        }

        /* A ResizeObserver on the statement itself, not a window resize
           handler. Window resize was rAF-throttled, and rAF is suspended while
           the tab is hidden, so a tab resized in the background kept a split
           measured at the old width. ResizeObserver is delivered off layout
           and catches it in any visible tab; for the hidden-tab case the
           intersection handler below re-splits before the visitor can see
           anything. */
        if (this.statementEl && 'ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.splitStatement);
          this.resizeObserver.observe(this.statementEl);
        }

        this.update();

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.classList.add('is-in', 'is-active');
                /* belt and braces: whatever happened while this was off-screen
                   or the tab was hidden, the split is correct by the time it
                   is seen */
                this.splitStatement();
                this.startCount();
                this.startVideo();
                window.addEventListener('scroll', this.onScroll, { passive: true });
                window.addEventListener('resize', this.onScroll, { passive: true });
                this.update();
              } else {
                this.classList.remove('is-active');
                window.removeEventListener('scroll', this.onScroll);
                window.removeEventListener('resize', this.onScroll);
              }
            });
          },
          /* start driving slightly before it is on screen so the first visible
             frame is already at the right progress rather than snapping */
          { rootMargin: '15% 0px 15% 0px' }
        );

        this.observer.observe(this);

        /* Failsafe. If the observer has still not fired by the time the page
           has settled and the section is already on screen, reveal it anyway.
           Cheap insurance against the one bug class that matters here: content
           that never appears. */
        this.safety = setTimeout(() => {
          if (this.classList.contains('is-in')) return;
          const rect = this.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top < vh && rect.bottom > 0) {
            this.classList.add('is-in');
            this.startCount();
            this.startVideo();
          }
        }, 2000);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.frame) cancelAnimationFrame(this.frame);
        if (this.safety) clearTimeout(this.safety);
        this.initialised = false;
      }

      /* ---- progress ----------------------------------------------------- */

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        this.frame = requestAnimationFrame(this.update);
      }

      /* 0 when the top edge sits at the bottom of the viewport, 1 when the
         bottom edge has passed the top of it. Read and write happen in the
         same frame, so this costs at most one forced layout per frame. */
      update() {
        this.ticking = false;
        this.frame = null;

        const rect = this.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        const travel = vh + rect.height;
        let p = travel > 0 ? (vh - rect.top) / travel : 1;

        if (p < 0) p = 0;
        if (p > 1) p = 1;

        this.style.setProperty('--p', p.toFixed(4));
      }

      /* ---- line splitting ------------------------------------------------ */

      splitStatement() {
        const el = this.statementEl;
        if (!el) return;

        const width = Math.round(el.clientWidth);
        if (!width) return;
        if (width === this.lastWidth && el.classList.contains('is-split')) return;
        this.lastWidth = width;

        /* the original text is kept on the element, so re-splitting always
           starts from the source rather than from the previous split */
        const source = el.dataset.text || el.textContent.replace(/\s+/g, ' ').trim();
        el.dataset.text = source;

        const words = source.split(' ').filter(Boolean);
        if (!words.length) return;

        /* pass 1: every word in its own inline span, so the browser wraps them
           and we can read where the line breaks actually landed.

           The spans are nowrap because the finished lines are nowrap. Without
           it a hyphenated word could break inside its own span, so
           "pack-and-go" was recorded as belonging to the line its first half
           landed on, and that line then rendered 60px wider than the column it
           was measured against. Measure the way it will be drawn. */
        el.classList.remove('is-split');
        el.textContent = '';
        const probe = document.createDocumentFragment();
        const spans = words.map((word) => {
          const span = document.createElement('span');
          span.style.whiteSpace = 'nowrap';
          span.textContent = word;
          return span;
        });
        spans.forEach((span, i) => {
          probe.appendChild(span);
          if (i < spans.length - 1) probe.appendChild(document.createTextNode(' '));
        });
        el.appendChild(probe);

        /* one batch of reads, no interleaved writes */
        const tops = spans.map((span) => span.offsetTop);

        const lines = [];
        let current = null;
        tops.forEach((top, i) => {
          if (current === null || Math.abs(top - current) > 2) {
            lines.push([]);
            current = top;
          }
          lines[lines.length - 1].push(words[i]);
        });

        /* pass 2: one element per rendered line, each with its slice of the
           reveal. The CSS holds these on one line: a group that fills the
           column exactly has no slack left, and sub-pixel rounding was enough
           to wrap it again, which put two visual lines inside one reveal
           box. */
        el.textContent = '';
        const out = document.createDocumentFragment();
        lines.forEach((line, i) => {
          const lineEl = document.createElement('span');
          lineEl.className = 'stat-reveal__line';
          lineEl.style.setProperty('--i', i);
          lineEl.style.setProperty('--n', lines.length);
          lineEl.textContent = line.join(' ');
          out.appendChild(lineEl);
        });
        el.appendChild(out);
        el.classList.add('is-split');
      }

      /* ---- stats --------------------------------------------------------- */

      startCount() {
        if (this.counted || !this.valueEls.length) return;
        this.counted = true;
        this.valueEls.forEach((el) => this.countOne(el));
      }

      countOne(el) {
        const raw = (el.dataset.to || '').trim();
        const to = Number(raw);

        /* only count when the whole label is a number. "90" counts, "90+" and
           "9.8k" are left exactly as the merchant typed them. */
        if (raw === '' || !isFinite(to)) return;

        const decimals = (raw.split('.')[1] || '').length;
        const duration = parseInt(el.dataset.duration, 10) || 1000;
        const start = performance.now();

        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          el.textContent = (to * eased).toFixed(decimals);
          if (t < 1) requestAnimationFrame(step);
        };

        el.textContent = (0).toFixed(decimals);
        requestAnimationFrame(step);
      }

      showFinalValues() {
        this.valueEls.forEach((el) => {
          if (el.dataset.to) el.textContent = el.dataset.to;
        });
      }

      /* ---- video ---------------------------------------------------------
         The <video> ships with preload="none" and no autoplay attribute, so
         not a byte is fetched until this runs. That is the whole point: a
         hero-weight file three screens down should not compete with anything
         above it.
         ------------------------------------------------------------------ */

      startVideo() {
        if (!this.video || this.videoStarted) return;
        this.videoStarted = true;
        this.video.muted = true;
        const played = this.video.play();
        /* a rejected autoplay is normal, not an error: the poster stays up */
        if (played && typeof played.catch === 'function') played.catch(() => {});
      }
    }
  );
}
